import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import {
    forceReloadModels,
    forceReloadCustomers,
    updateModelsManagementStorage,
    reloadModelsManagement,
} from '@hooks/API/Contact/UseContactsList';

const getContactByID = (ID) => Axios.get(`/contacts/${ID}`);

const useContact = (ID) => {
    //convert ID to number using parseInt and get a boolean if it's a number
    const disableQuery = !ID || isNaN(parseInt(ID));
    const intID = !disableQuery ? parseInt(ID) : null;
    return useQuery({
        queryKey: ['contact', intID],
        queryFn: () => getContactByID(intID),
        enabled: !disableQuery,
        staleTime: 60000,
        cacheTime: 60000,
        retry: false,
    });
};

const forceReloadContact = (ID) => {
    const intID = Number(ID);
    queryClient.invalidateQueries({ queryKey: ['contact', intID] });
};

const name = (contact) => {
    const cn = contact.Surname ? `${contact.Name} ${contact.Surname}` : contact.Name;
    return cn;
};

const onCheckDuplicates = ({ type, data }) =>
    Axios.post(`${type === 1 ? '/models' : '/customers'}/CheckDuplicates`, data);

const useCheckDuplicates = () => {
    return useMutation({
        mutationFn: onCheckDuplicates,
        onSuccess: (data, variables) => {
            // forceReloadContact(variables.data.ID);
        },
    });
};

const onUpdateContact = ({ type, ID, data }) => Axios.put(`${type === 1 ? '/models' : '/customers'}/${ID}`, data);

const useUpdateContact = () => {
    return useMutation({
        mutationFn: onUpdateContact,
        onSuccess: (data, variables) => {
            forceReloadContact(variables.ID);
            if (variables.type === 1) {
                forceReloadModels();
            } else {
                forceReloadCustomers();
            }
        },
    });
};

const onUpdateContactCustomFields = ({ contactId, data }) => Axios.put(`/contacts/Customfields/${contactId}`, data);

const useUpdateContactCustomFields = () => {
    return useMutation({
        mutationFn: onUpdateContactCustomFields,
        onSuccess: (data, variables) => {
            forceReloadContact(variables.contactId);
        },
    });
};

const onRemoveContactCover = ({ contactId }) => Axios.delete(`/contacts/removeCover/${contactId}`);

const useRemoveContactCover = () => {
    return useMutation({
        mutationFn: onRemoveContactCover,
        onSuccess: (data, variables) => {
            forceReloadContact(variables.contactId);
        },
    });
};

const onArchiveContact = ({ contactId, networkAgencyId }) => {
    return Axios.post(`/contacts/store`, {
        ContactID: contactId,
        NetworkAgencyID: networkAgencyId,
    });
};

const syncManagementModelStorage = (variables, storageBooking) => {
    if (variables.contactType !== 'Model' || variables.area !== 'management') {
        return;
    }
    updateModelsManagementStorage(variables.contactId, variables.networkAgencyId, storageBooking);
    reloadModelsManagement();
};

const useArchiveContact = () => {
    return useMutation({
        mutationFn: onArchiveContact,
        onSuccess: (data, variables) => {
            forceReloadContact(variables.contactId);
            if (variables.contactType === 'Model') {
                forceReloadModels();
                syncManagementModelStorage(variables, true);
            } else {
                forceReloadCustomers();
                queryClient.setQueryData(['customer-wall-ids', variables.area], null);
            }
        },
    });
};

const onUnarchiveContact = ({ contactId, networkAgencyId }) =>
    Axios.post(`/contacts/unstore`, {
        ContactID: contactId,
        NetworkAgencyID: networkAgencyId,
    });

const useUnarchiveContact = () => {
    return useMutation({
        mutationFn: onUnarchiveContact,
        onSuccess: (data, variables) => {
            forceReloadContact(variables.contactId);
            if (variables.contactType === 'Model') {
                forceReloadModels();
                syncManagementModelStorage(variables, false);
            } else {
                forceReloadCustomers();
            }
        },
    });
};

export {
    useContact,
    forceReloadContact,
    name,
    useCheckDuplicates,
    useUpdateContact,
    useUpdateContactCustomFields,
    useRemoveContactCover,
    useArchiveContact,
    useUnarchiveContact,
    onUpdateContactCustomFields,
    onCheckDuplicates,
    onUpdateContact,
};
